/* Conversations de l'utilisateur connecté (/conversations) : ses propres fils,
   avec recherche plein texte dans ses transcriptions. Atteignable depuis le rail
   enseignant.

   Ne liste QUE les fils dont il est le sujet : ceux qu'un responsable tient pour
   un enfant portent `classroom_student_id` et sont exclus par `listForUser`. Ils
   se consultent sur /tutor/enfant/<id>/conversations, où le lien parent↔enfant
   est vérifié.

   Exclut aussi mode='cours' : ces fils sont les sidechats de l'éditeur de
   contenu, un par document, et se consultent depuis la bibliothèque
   (/teacher/library/<id>), pas depuis une liste générique. Mesuré en prod : un
   compte actif portait 41 fils 'cours' contre 17 'general', triés par activité
   récente — les sidechats noyaient les vraies conversations dans cette liste. */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '../lib/routing';
import { PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { searchMyMessages } from '../lib/message-search';
import { createThread, deleteThread, listThreads, renameThread, type ChatThread } from '../lib/chat-api';
import { threadRoute } from '../lib/thread-route';
import { newConversationRoute } from '../lib/new-conversation-route';
import { useRoles } from '../hooks/use-roles';
import { relativeFr } from '../lib/humanize';
import { ShowTestDataToggle, useShowTestData } from '../hooks/use-show-test-data';

interface Hit {
  thread_id: string;
  snippet: string;
}

export default function Conversations() {
  const [threads, setThreads] = useState<ChatThread[] | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState<ChatThread | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAdmin, includeTestData, setIncludeTestData } = useShowTestData();
  const { activeRole, children: linkedChildren, activeStudentId, loading: rolesLoading } = useRoles();

  useEffect(() => {
    /* Doit rester identique à CONVERSATIONS_LIST_EXCLUDED_MODE (packages/application/src/chat/list-threads.ts) : le compteur du dashboard en dépend. Non importable depuis apps/web. */
    listThreads(100, 'cours', includeTestData).then(setThreads).catch((e) => {
      console.error('[conversations] listThreads failed', e);
      setThreads([]);
    });
  }, [includeTestData]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (debounced.length < 2) {
      setHits(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    (async () => {
      let data: Awaited<ReturnType<typeof searchMyMessages>>;
      try {
        data = await searchMyMessages(debounced);
      } catch (e) {
        if (cancelled) return;
        console.error('[conversations] search failed', (e as Error).message);
        setHits([]);
        setSearching(false);
        return;
      }
      if (cancelled) return;
      const seen = new Set<string>();
      const out: Hit[] = [];
      for (const r of (data ?? []) as Array<{ thread_id: string | null; transcript: string }>) {
        if (!r.thread_id || seen.has(r.thread_id)) continue;
        seen.add(r.thread_id);
        out.push({ thread_id: r.thread_id, snippet: makeSnippet(r.transcript, debounced) });
      }
      setHits(out);
      setSearching(false);
    })();
    return () => { cancelled = true; };
  }, [debounced]);

  const threadById = useMemo(() => {
    const m = new Map<string, ChatThread>();
    (threads ?? []).forEach((t) => m.set(t.id, t));
    return m;
  }, [threads]);

  const isSearch = debounced.length >= 2;

  const newConversation = async () => {
    setError(null);

    /* Chat prof / chat élève créent eux-mêmes leur fil, avec le bon mode et le
       bon périmètre : un fil `general` poussé dans ces surfaces y serait illisible. */
    const dedicated = newConversationRoute({
      activeRole,
      children: linkedChildren,
      activeStudentId,
    });
    if (dedicated) {
      navigate(dedicated);
      return;
    }

    setCreating(true);
    try {
      const t = await createThread({ mode: 'general' });
      navigate(threadRoute(t) ?? `/c/${t.id}`);
    } catch (e) {
      console.error('[conversations] createThread failed', e);
      setError("La nouvelle conversation n'a pas pu être créée. Réessaie dans un instant.");
      setCreating(false);
    }
  };

  const confirmDeletion = async () => {
    const target = pendingDeletion;
    if (!target || deleting) return;
    setError(null);
    setDeleting(true);
    try {
      await deleteThread(target.id);
      setThreads((prev) => (prev ?? []).filter((x) => x.id !== target.id));
      setHits((prev) => (prev ?? []).filter((h) => h.thread_id !== target.id));
      setPendingDeletion(null);
    } catch (e) {
      console.error('[conversations] deleteThread failed', e);
      setError("La conversation n'a pas pu être supprimée. Réessaie dans un instant.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        actions={
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 px-3 max-sm:w-11 max-sm:px-0"
            onClick={() => void newConversation()}
            disabled={creating || rolesLoading}
            aria-label="Nouvelle conversation"
          >
            <Plus size={15} />
            <span className="hidden sm:inline">Nouvelle</span>
          </Button>
        }
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Mes conversations</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Retrouvez et recherchez dans toutes vos discussions avec Naïa.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[720px] mx-auto px-5 pb-16 pt-4">
        {isAdmin && <ShowTestDataToggle checked={includeTestData} onChange={setIncludeTestData} />}
        {error && <Alert>{error}</Alert>}

        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher dans mes conversations…"
          aria-label="Rechercher dans mes conversations"
          className="w-full mt-[18px] px-[14px] py-3 min-h-[46px] border border-border rounded-sm bg-card text-foreground text-[15px] font-[inherit]"
        />

        <div className="mt-[18px]">
          {isSearch ? (
            searching && hits === null ? (
              <p className="text-muted-foreground py-3">Recherche…</p>
            ) : !hits || hits.length === 0 ? (
              <p className="text-muted-foreground py-3">
                Aucun résultat pour « {debounced} ».
              </p>
            ) : (
              <div className="bg-card border border-border rounded-card overflow-hidden shadow-card">
                {hits.map((h) => {
                  const t = threadById.get(h.thread_id);
                  return (
                    <Link
                      key={h.thread_id}
                      /* Le résultat de recherche ne connaît que l'identifiant :
                         le fil correspondant porte le mode, donc la destination. */
                      to={(t && threadRoute(t)) ?? `/c/${h.thread_id}`}
                      className="block px-[14px] py-3 border-b border-border no-underline"
                    >
                      <div className="font-bold text-[15px] text-foreground">{t?.title || 'Conversation'}</div>
                      <div className="text-[13px] text-muted-foreground mt-0.5">{h.snippet}</div>
                    </Link>
                  );
                })}
              </div>
            )
          ) : threads === null ? (
            <p className="text-muted-foreground py-3">Chargement…</p>
          ) : threads.length === 0 ? (
            <p className="text-muted-foreground py-3">
              Pas encore de conversation. Lance-toi !
            </p>
          ) : (
            <div className="bg-card border border-border rounded-card overflow-hidden shadow-card">
              {threads.map((t) => (
                <ConvRow
                  key={t.id}
                  thread={t}
                  onRenamed={(title) => setThreads((prev) => (prev ?? []).map((x) => (x.id === t.id ? { ...x, title } : x)))}
                  onDelete={() => setPendingDeletion(t)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <DeleteThreadDialog
        thread={pendingDeletion}
        busy={deleting}
        onCancel={() => setPendingDeletion(null)}
        onConfirm={confirmDeletion}
      />
    </>
  );
}

function ConvRow({
  thread,
  onRenamed,
  onDelete,
}: {
  thread: ChatThread;
  onRenamed: (title: string) => void;
  onDelete: () => void;
}) {
  const rename = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const name = window.prompt('Renommer la conversation :', thread.title);
    if (name === null || !name.trim()) return;
    onRenamed(name.trim().slice(0, 120));
    try { await renameThread(thread.id, name); } catch (err) { console.error('[conversations] rename failed', err); }
  };
  const askDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete();
  };
  return (
    <div className="flex items-center border-b border-border">
      <Link to={threadRoute(thread) ?? `/c/${thread.id}`} className="flex-1 min-w-0 flex flex-col gap-0.5 px-[14px] py-3 no-underline text-foreground">
        <span className="font-bold text-[15px] line-clamp-2 break-words" title={thread.title}>{thread.title || 'Conversation'}</span>
        <span className="text-xs text-muted-foreground">{relativeFr(thread.last_message_at)}</span>
      </Link>
      {/* Cible tactile : 42×23 mesurés au rendu avant correction, sous le seuil
          de 44×44 sur chaque ligne et à toutes les tailles. */}
      <button
        type="button"
        onClick={rename}
        title="Renommer"
        aria-label={`Renommer la conversation ${thread.title || 'Conversation'}`}
        className="shrink-0 grid place-items-center w-11 h-11 bg-transparent border-none cursor-pointer text-muted-foreground"
      >
        <PencilSimple size={17} />
      </button>
      <button
        type="button"
        onClick={askDelete}
        title="Supprimer"
        aria-label={`Supprimer la conversation ${thread.title || 'Conversation'}`}
        className="shrink-0 grid place-items-center w-11 h-11 bg-transparent border-none cursor-pointer text-muted-foreground hover:text-destructive"
      >
        <Trash size={17} />
      </button>
    </div>
  );
}

function DeleteThreadDialog({
  thread,
  busy,
  onCancel,
  onConfirm,
}: {
  thread: ChatThread | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={thread !== null} onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <DialogContent style={{ maxWidth: 460 }} aria-describedby="delete-thread-desc">
        <DialogHeader>
          <DialogTitle>Supprimer cette conversation ?</DialogTitle>
        </DialogHeader>
        <div id="delete-thread-desc" className="px-5 py-4 text-[14px] leading-snug text-muted-foreground">
          <p className="m-0">
            « {thread?.title || 'Conversation'} » disparaîtra de vos conversations et de vos recherches. C'est
            irréversible.
          </p>
          <p className="m-0 mt-2">
            La transcription reste conservée côté administration au titre de la protection des mineurs : elle n'est
            plus consultable depuis l'application, mais elle n'est pas effacée de nos serveurs.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <Button variant="outline" className="min-h-11" onClick={onCancel} disabled={busy}>
            Annuler
          </Button>
          <Button variant="destructive" className="min-h-11" onClick={onConfirm} disabled={busy}>
            {busy ? 'Suppression…' : 'Supprimer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function makeSnippet(text: string, q: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  const i = clean.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return clean.slice(0, 120) + (clean.length > 120 ? '…' : '');
  const start = Math.max(0, i - 40);
  const end = Math.min(clean.length, i + q.length + 80);
  return (start > 0 ? '…' : '') + clean.slice(start, end) + (end < clean.length ? '…' : '');
}
